import cn from 'clsx'
import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { createNestedSetLocation } from '@disco/utils/nesting'
import { selectionContainNestedTracks } from '@disco/utils/selection'
import { SelectableItem } from '@disco/types/types/selection'

const itemClass = (nestedTrackHaveSelected: boolean, disabled?: boolean) => {
  return cn('context-menu__option', {
    'is-disabled': nestedTrackHaveSelected || disabled,
  })
}

export const getExplorerEnabledClasses = (props: {
  selectedItems: SelectableItem[]
  selectedPlaylists: Playlist[]
  selectedTracks: Track[]
  selectedTracksWithinUserTerritories: Track[]
  selectedLibrary: Group | null | undefined
  selectedPane: ExplorerPane | null | undefined
  selectionGroup: Record<string, any>
  lockContent: boolean
}): {
  channelActionEnabledClass: string
  copyMetadataActionEnabledClass: string
  deleteActionEnabledClass: string
  mixedActionEnabledClass: string
  removeFromChannelClass: string
  playlistActionEnabledClass: string
  trackActionEnabledClass: string
  mergeEnabledClass: string
  inboxMixedActionEnabledClass: string
  inboxTrackActionEnabledClass: string
  manageNestingClass: string
} => {
  const {
    selectedItems,
    selectedPlaylists,
    selectedTracks,
    selectedTracksWithinUserTerritories,
    selectedLibrary,
    selectedPane,
    selectionGroup,
    lockContent,
  } = props
  const nestedTrackHaveSelected =
    selectionContainNestedTracks(selectedItems) && selectedItems.length > 1
  const boundItemClass = itemClass.bind(null, nestedTrackHaveSelected)
  const inboxActionsShown =
    selectedLibrary && INBOX_TYPES.indexOf(selectedLibrary.group_type) > -1
  const inboxActionsDisabled =
    inboxActionsShown && (!selectedPane || selectedPane.index > 1)
  const channelActionEnabledClass = boundItemClass(
    selectedTracksWithinUserTerritories.length === 0 &&
      selectedPlaylists.length === 0,
  )
  const copyMetadataActionEnabledClass = boundItemClass(
    (selectedTracksWithinUserTerritories.length === 0 &&
      selectedPlaylists.length === 0) ||
      selectedPlaylists.length === selectedItems.length,
  )
  const deleteDisabled =
    lockContent ||
    (selectedTracksWithinUserTerritories.length === 0 &&
      selectedPlaylists.length === 0)
  // Only delete option available if selectedItems contains nested tracks
  const deleteActionEnabledClass = cn(itemClass(deleteDisabled), {
    'context-menu__option--warning': !deleteDisabled,
  })
  const mixedActionEnabledClass = boundItemClass(selectedItems.length === 0)
  const removeFromChannelEnabled =
    selectionGroup &&
    selectionGroup.group_type === CUSTOM_CHANNEL &&
    (selectedPlaylists.length > 0 ||
      selectedTracksWithinUserTerritories.length > 0)
  const removeFromChannelClass = boundItemClass(
    !removeFromChannelEnabled || lockContent,
  )
  const playlistActionEnabledClass = boundItemClass(
    selectedPlaylists.length === 0 || selectedTracks.length > 0,
  )
  const trackActionEnabledClass = itemClass(
    false,
    selectedTracksWithinUserTerritories.length === 0 ||
      selectedPlaylists.length > 0,
  )
  const mergeEnabledClass = boundItemClass(
    selectedTracksWithinUserTerritories.length < 2 ||
      selectedPlaylists.length > 0,
  )
  const inboxMixedActionEnabledClass = boundItemClass(
    (selectedTracksWithinUserTerritories.length === 0 &&
      selectedPlaylists.length === 0) ||
      inboxActionsDisabled,
  )
  const inboxTrackActionEnabledClass = boundItemClass(
    selectedTracksWithinUserTerritories.length === 0 ||
      selectedPlaylists.length > 0 ||
      inboxActionsDisabled,
  )
  const manageNestingClass = itemClass(
    false,
    !Boolean(createNestedSetLocation(selectedTracks)) ||
      selectedPlaylists.length > 0,
  )
  return {
    channelActionEnabledClass,
    copyMetadataActionEnabledClass,
    deleteActionEnabledClass,
    mixedActionEnabledClass,
    removeFromChannelClass,
    playlistActionEnabledClass,
    trackActionEnabledClass,
    mergeEnabledClass,
    inboxMixedActionEnabledClass,
    inboxTrackActionEnabledClass,
    manageNestingClass,
  }
}
